import { useEffect, useState } from "react";
import { getDomain } from "tldjs";
import { capitalize } from "lodash";
import { Row, Col, Button } from "antd";
import { settings } from "imagica-corekit/dist/base/kernel/Settings";
import { CurrentNodeType } from "../typing";
import { ResizeParams as ResizeEventParams } from "@reactflow/node-resizer";
import style from "./AskBrain.module.css";

type canvasType = "canvas" | "saas";

type Brain = {
  answer: string;
  latency: any;
  query: string;
  sources_data: Link[];
};

type Link = {
  domain_name: string;
  favicon: string;
  index: number;
  source: string;
};

type AskBrainProps = {
  id: string;
  canvasType: canvasType;
  textAreaValue: Brain;
  resizeEventParams?: ResizeEventParams;
  enableFeedback?: boolean;
} & CurrentNodeType;

export const AskBrain: React.FC<AskBrainProps> = props => {
  const [answerHtml, setAnswerHtml] = useState("");

  const [links, setLinks] = useState<Link[]>([]);

  const getTextNodeClass = () => {
    const classes = [
      `${style["custom-node"]} ${style["design-text"]}`,
      `${style["guide-textnode-disable"]}`,
      `${props.enableFeedback ? style["target"] : ""}`,
      `${props.noWheelClassName}`,
    ];
    return classes.join(" ");
  };

  useEffect(() => {
    setAnswerHtml(
      (props.textAreaValue?.answer ?? "").replace(
        /\[(\d)+\]/g,
        (match, p1) => '<span class="dangerous-html-dot">' + p1 + "</span>"
      )
    );
  }, [props.textAreaValue?.answer]);

  useEffect(() => {
    const links = (props.textAreaValue?.sources_data ?? [])
      .map((x, index) => {
        // 兼容旧的数据
        if (typeof x === "string") {
          return {
            domain_name: x,
            favicon: "",
            index: index + 1,
            source: x,
          };
        }
        return x;
      })
      .sort((a, b) => a.index - b.index);

    setLinks(links);
  }, [props.textAreaValue?.sources_data]);

  return (
    <div className={getTextNodeClass()}>
      <div
        className={`${style["text-content"]} ${style[!props.isShare ? "text-default-padding" : "text-reset-padding"]}`}
      >
        <div className={style.html_content} dangerouslySetInnerHTML={{ __html: answerHtml }} />
        <Row gutter={8} className={style["link-box"]}>
          {links.map((link, index) => (
            <Col key={index} className={style["link-item-col"]}>
              <LinkItem link={link} canvasType={props.canvasType} />
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
};

const LinkItem = ({ link, canvasType }: { link: Link; canvasType: canvasType }) => {
  const { index, source, domain_name, favicon } = link;

  const defaultLogo = `${settings.S3CDN}${settings.viewAssetsPath}default-link-logo.png`;

  const domain = capitalize((getDomain(domain_name) ?? "").split(".")[0]);

  const handleOpen = (source: string) => {
    window.open(source, "__blank");
  };

  return (
    <div className={`${style["link-item"]} ${canvasType === "saas" ? style["link-item-saas"] : ""}`}>
      <span className={`${style["item-index"]}`}>{index}</span>
      <img
        className={style["item-logo"]}
        src={favicon ?? defaultLogo}
        alt="logo"
        onError={(e: any) => {
          e.target.src = defaultLogo;
        }}
      />
      <Button className={style["item-title"]} type="link" onClick={() => handleOpen(source)}>
        {domain ?? "-"}
      </Button>
    </div>
  );
};
